import React from "react";
import ReactDOM from "react-dom/client";
import { createHashHistory, createBrowserHistory } from "@tanstack/react-router";

import "@fontsource-variable/dm-sans/index.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@xterm/xterm/css/xterm.css";
import "./index.css";

import { browserBuildEnvironment, isElectron } from "./env";
import { resolveBrowserEntry } from "./environments/primary/browserEntry.logic";
import { readBrowserSessionState } from "./environments/primary/browserSession";
import { getRouter } from "./router";
import { syncDocumentWindowControlsOverlayClass } from "./lib/windowControlsOverlay";
import { AppRoot } from "./AppRoot";
import { readLocalApi } from "./localApi";
import { restoreBrowserAccount } from "./environmentAccess/browserAccount.logic";

// Electron loads the app from a file-backed shell, so hash history avoids path resolution issues.
const history = isElectron ? createHashHistory() : createBrowserHistory();

if (
  import.meta.env.VITE_AFAR_RESEARCH_PERFORMANCE_TARGET === "1" &&
  new URLSearchParams(window.location.search).get("afar-performance-target") === "host-web"
) {
  void import("./clientPerformanceTarget");
}
if (
  isElectron &&
  new URLSearchParams(window.location.search).get("afar-performance-target") === "desktop"
) {
  void import("./desktopClientPerformanceTarget");
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
const visualQualification =
  import.meta.env.VITE_AFAR_RESEARCH_VISUAL_QUALIFICATION === "1" &&
  new URLSearchParams(window.location.search).has("afar-visual-qualification");

if (visualQualification) {
  void import("./visualQualificationTarget").then(({ renderVisualQualificationTarget }) => {
    renderVisualQualificationTarget(root);
  });
} else {
  if (isElectron) {
    syncDocumentWindowControlsOverlayClass();
  }
  void resolveBrowserEntry({
    isElectron,
    env: browserBuildEnvironment,
    pathname: window.location.pathname,
    readSession: readBrowserSessionState,
    readAccount: () => restoreBrowserAccount(readLocalApi()?.connect),
  }).then((entry) => {
    if (entry === "sign-in") {
      void import("./components/BrowserSignInScreen").then(({ BrowserSignInScreen }) => {
        root.render(
          <React.StrictMode>
            <BrowserSignInScreen />
          </React.StrictMode>,
        );
      });
      return;
    }
    if (entry === "pair") {
      void import("./components/BrowserPairingScreen").then(({ BrowserPairingScreen }) => {
        root.render(
          <React.StrictMode>
            <BrowserPairingScreen />
          </React.StrictMode>,
        );
      });
      return;
    }
    const router = getRouter(history);
    root.render(
      <React.StrictMode>
        <AppRoot router={router} />
      </React.StrictMode>,
    );
  });
}
